// Single source of truth for the platform's four roles — MASTER_ADMIN,
// SCHOOL_ADMIN, TEACHER, STUDENT — plus the legacy lowercase "admin" role
// that predates the MASTER_ADMIN/SCHOOL_ADMIN split and is still issued to
// a handful of older accounts (privilege-equivalent to MASTER_ADMIN
// everywhere in this app).
//
// Every file that used to hardcode its own ROLE_LABELS/ROLE_COLORS map or
// its own role-comparison literals should import from here instead of
// adding another copy — see InvitedRegister.js, AdminDashboardV2.js,
// ConversationViewer.js, ChatSidebar.js, ChatHeader.js, Signin.js,
// StudentTutorSession.js, and CurriculumPage.js for the call sites this
// replaced.

export const ROLES = Object.freeze({
  MASTER_ADMIN: 'MASTER_ADMIN',
  SCHOOL_ADMIN: 'SCHOOL_ADMIN',
  TEACHER: 'TEACHER',
  STUDENT: 'STUDENT',
  // Legacy value, pre-dates the MASTER_ADMIN/SCHOOL_ADMIN split. Still
  // privilege-equivalent to MASTER_ADMIN everywhere this app checks role.
  LEGACY_ADMIN: 'admin',
});

export const ROLE_LABELS = {
  [ROLES.MASTER_ADMIN]: 'Master Admin',
  [ROLES.SCHOOL_ADMIN]: 'School Admin',
  [ROLES.TEACHER]: 'Teacher',
  [ROLES.STUDENT]: 'Student',
  [ROLES.LEGACY_ADMIN]: 'Admin',
};

// Hex colors for every role badge in the app — InvitedRegister's role-
// colored circle, the Help & Guides cards, and every role Chip (via the
// shared <RoleChip> in components/RoleChip.js, styled with `sx` rather than
// MUI's closed `color` enum, which can't express teal). One map, one system:
// there used to be a second MUI-`color`-keyword map (ROLE_CHIP_COLOR) for
// ConversationViewer's Chip specifically, which is how Teacher ended up
// teal everywhere except that one screen, where 'success' rendered MUI's
// default green — the same green already used on those screens for the
// "Granted" access chip and all-clear safety tiles, so a Teacher chip read
// as a status, not a role. Deleted; every role-chip consumer goes through
// RoleChip now.
//
// Teacher and School Admin are deliberately different hues — not two shades
// of the same one — since they sit side by side in every user table and
// must read as different roles at a glance.
//
// Contrast against white (both as text-on-white and as the chip's
// border/text on its own near-white background — see RoleChip.js for why
// the chip has no tinted fill):
//   #7b1fa2 (Master Admin, purple) — 8.2:1
//   #1976d2 (School Admin, blue)   — 4.6:1
//   #0f766e (Teacher, teal)        — 5.47:1
//   #b45309 (Student, amber)       — 5.02:1
// All four clear WCAG AA (4.5:1) for normal text. Student was #f57c00
// (2.7:1) until RoleChip started using ROLE_COLORS as real foreground/
// border color rather than a decorative fill only — #f57c00 failed AA in
// both directions (orange-on-white and white-on-orange) and was never
// actually verified for text use, unlike teal/blue above. #b45309 is the
// same hue family, darkened until it passes.
export const ROLE_COLORS = {
  [ROLES.MASTER_ADMIN]: '#7b1fa2',
  [ROLES.SCHOOL_ADMIN]: '#1976d2',
  [ROLES.TEACHER]: '#0f766e',
  [ROLES.STUDENT]: '#b45309',
  [ROLES.LEGACY_ADMIN]: '#7b1fa2',
};

export const isMasterAdmin = (role) => role === ROLES.MASTER_ADMIN || role === ROLES.LEGACY_ADMIN;
export const isSchoolAdmin = (role) => role === ROLES.SCHOOL_ADMIN;
export const isTeacher = (role) => role === ROLES.TEACHER;
export const isStudent = (role) => role === ROLES.STUDENT;

// "Admin" in the strict sense this app already used it in: can change
// roles/school/access, delete users, and manage school settings.
// Deliberately excludes TEACHER — a teacher is scoped to their own classes
// and can't do any of that. See isStaffRole for the broader question.
export const isAdminRole = (role) => isMasterAdmin(role) || isSchoolAdmin(role);

// Broader "has a staff seat, not a student" check — includes teachers. This
// is what decides admin-dashboard access (the sidebar/header "Admin
// Dashboard" link), whether a tutor session is a preview vs. a real student
// session, etc. — anywhere the real question is "student or not?", not
// "admin or not?".
export const isStaffRole = (role) => isAdminRole(role) || isTeacher(role);

// Only a master or school admin can change roles, schools, or access, or
// delete a user — a teacher never can, not even for their own students.
export const canManageRoles = (role) => isAdminRole(role);

// Only a master or school admin can assign/unassign teachers on a class —
// a teacher can never do this, not even to assign themselves.
export const canManageTeachers = (role) => isAdminRole(role);

// SCHOOL_ADMIN and TEACHER both belong to exactly one, fixed school: forms
// auto-fill it and disable the field rather than letting them pick one.
export const isSchoolScopedRole = (role) => isSchoolAdmin(role) || isTeacher(role);

// Roles `currentRole` may assign to another user or invite by email,
// ordered low-to-high privilege. Empty for a student (or unrecognized
// role). Shared by role-assignment dropdowns and role-filter dropdowns —
// the two ask the same underlying question ("which roles does this viewer
// have a legitimate reason to see/pick from the list?").
export function assignableRoles(currentRole) {
  if (isMasterAdmin(currentRole)) {
    return [ROLES.STUDENT, ROLES.TEACHER, ROLES.SCHOOL_ADMIN, ROLES.MASTER_ADMIN];
  }
  if (isSchoolAdmin(currentRole)) {
    return [ROLES.STUDENT, ROLES.TEACHER, ROLES.SCHOOL_ADMIN];
  }
  if (isTeacher(currentRole)) {
    return [ROLES.STUDENT];
  }
  return [];
}
